import { useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Box, Button, Card, CardContent, Chip, Dialog, DialogContent, DialogTitle,
  Divider, Grid, IconButton, Stack, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, TablePagination, TextField,
  Tooltip, Typography, Paper, alpha, useTheme, Alert,
} from '@mui/material';
import {
  Block, CheckCircle, Close, People, DirectionsCar, Star,
  TwoWheeler, EmojiEvents,
} from '@mui/icons-material';
import PageShell from '../../components/layout/PageShell';
import PageHeader from '../../components/layout/PageHeader';
import api from '../../services/api';
import { formatMoney } from '../../utils/currency';

interface Driver {
  _id: string;
  firstName?: string;
  lastName?: string;
  phone?: string;
  email?: string;
  partnerStatus?: string;
  isActive?: boolean;
  isVerified?: boolean;
  isOnline?: boolean;
  isOnTrip?: boolean;
  rating?: number;
  zoneId?: { name?: string; code?: string };
  vehicleDetails?: { model?: string; number?: string; type?: string };
  walletBalance?: number;
  createdAt?: string;
}

const STATUS_COLOR: Record<string, 'default' | 'success' | 'warning' | 'error' | 'info'> = {
  online: 'success',
  on_trip: 'warning',
  offline: 'default',
  blocked: 'error',
};

function driverOnlineStatus(d: Driver): string {
  if (d.partnerStatus === 'blocked') return 'blocked';
  if (d.isOnTrip) return 'on_trip';
  if (d.isOnline) return 'online';
  return 'offline';
}

interface StatCardProps { label: string; value: number; color?: string; icon?: React.ReactNode }

function StatCard({ label, value, color, icon }: StatCardProps) {
  return (
    <Card elevation={0} sx={{ borderRadius: 2.5, border: 1, borderColor: 'divider', height: '100%' }}>
      <CardContent>
        <Stack direction="row" alignItems="center" spacing={1.5}>
          {icon && <Box sx={{ color: color || 'primary.main', display: 'flex' }}>{icon}</Box>}
          <Box>
            <Typography variant="h5" fontWeight={800} color={color || 'text.primary'}>{value}</Typography>
            <Typography variant="caption" color="text.secondary">{label}</Typography>
          </Box>
        </Stack>
      </CardContent>
    </Card>
  );
}

export default function DriversPage() {
  const theme = useTheme();
  const queryClient = useQueryClient();
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(25);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [detailDriver, setDetailDriver] = useState<Driver | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ['transport-drivers', search, statusFilter, page, pageSize],
    queryFn: async () => {
      const res = await api.get('/partners', {
        params: {
          entityType: 'driver',
          serviceHub: 'transport',
          ...(search ? { search } : {}),
          ...(statusFilter ? { partnerStatus: statusFilter } : {}),
          page: page + 1,
          limit: pageSize,
        },
      });
      return res.data;
    },
  });

  const drivers: Driver[] = data?.data || [];
  const total: number = data?.total || 0;

  const totalCount = total;
  const onlineCount = drivers.filter((d) => d.isOnline && !d.isOnTrip).length;
  const onTripCount = drivers.filter((d) => d.isOnTrip).length;
  const availableCount = drivers.filter((d) => d.isOnline && !d.isOnTrip && d.isActive !== false).length;

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['transport-drivers'] });

  const blockMutation = useMutation({
    mutationFn: async ({ id, block }: { id: string; block: boolean }) => {
      await api.put(`/partners/${id}`, { partnerStatus: block ? 'blocked' : 'active' });
    },
    onSuccess: invalidate,
  });

  const approveMutation = useMutation({
    mutationFn: async (id: string) => {
      await api.put(`/partners/${id}`, { isVerified: true, partnerStatus: 'active' });
    },
    onSuccess: invalidate,
  });

  return (
    <PageShell>
      <PageHeader
        eyebrow="Transport"
        title="Driver Management"
        subtitle="View, approve, and manage transport drivers — online status, trips, and documents."
        gradient
        actions={
          <Button component={RouterLink} to="/transport/performance" variant="contained" startIcon={<EmojiEvents />}>
            Leaderboard & incentives
          </Button>
        }
      />

      {/* Stats */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={6} sm={3}>
          <StatCard label="Total Drivers" value={totalCount} icon={<People />} />
        </Grid>
        <Grid item xs={6} sm={3}>
          <StatCard label="Online" value={onlineCount} color="success.main" icon={<CheckCircle />} />
        </Grid>
        <Grid item xs={6} sm={3}>
          <StatCard label="On Trip" value={onTripCount} color="warning.main" icon={<DirectionsCar />} />
        </Grid>
        <Grid item xs={6} sm={3}>
          <StatCard label="Available" value={availableCount} color="info.main" icon={<TwoWheeler />} />
        </Grid>
      </Grid>

      {/* Filters */}
      <Box sx={{ mb: 2, p: 2, borderRadius: 2, border: 1, borderColor: 'divider' }}>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} flexWrap="wrap" useFlexGap>
          <TextField
            size="small"
            label="Search"
            placeholder="Name, phone, email…"
            value={search}
            onChange={(e) => { setSearch(e.target.value); setPage(0); }}
            sx={{ minWidth: 240 }}
          />
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            {['', 'active', 'pending', 'blocked'].map((s) => (
              <Chip
                key={s}
                label={s || 'All'}
                onClick={() => { setStatusFilter(s); setPage(0); }}
                color={statusFilter === s ? 'primary' : 'default'}
                variant={statusFilter === s ? 'filled' : 'outlined'}
                size="small"
              />
            ))}
          </Stack>
        </Stack>
      </Box>

      {isLoading ? (
        <Alert severity="info">Loading drivers…</Alert>
      ) : (
        <Paper elevation={0} sx={{ borderRadius: 2.5, border: 1, borderColor: 'divider', overflow: 'hidden' }}>
          <TableContainer>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 700, width: 56 }}>S.No</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Name</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Phone</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Vehicle</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Zone</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Rating</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Status</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Docs</TableCell>
                  <TableCell sx={{ fontWeight: 700, width: 100 }} align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {drivers.map((driver, idx) => {
                  const onlineStatus = driverOnlineStatus(driver);
                  return (
                    <TableRow
                      key={driver._id}
                      hover
                      sx={{ cursor: 'pointer', '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.03) } }}
                      onClick={() => setDetailDriver(driver)}
                    >
                      <TableCell>{page * pageSize + idx + 1}</TableCell>
                      <TableCell>
                        <Typography variant="body2" fontWeight={600}>
                          {driver.firstName} {driver.lastName}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">{driver.email || '—'}</Typography>
                      </TableCell>
                      <TableCell>{driver.phone || '—'}</TableCell>
                      <TableCell>
                        {driver.vehicleDetails?.model ? (
                          <Box>
                            <Typography variant="body2">{driver.vehicleDetails.model}</Typography>
                            <Typography variant="caption" color="text.secondary">{driver.vehicleDetails.number}</Typography>
                          </Box>
                        ) : '—'}
                      </TableCell>
                      <TableCell>{driver.zoneId?.name || '—'}</TableCell>
                      <TableCell>
                        {driver.rating != null ? (
                          <Stack direction="row" alignItems="center" spacing={0.5}>
                            <Star sx={{ fontSize: 14, color: 'warning.main' }} />
                            <Typography variant="body2">{driver.rating.toFixed(1)}</Typography>
                          </Stack>
                        ) : '—'}
                      </TableCell>
                      <TableCell>
                        <Chip size="small" label={onlineStatus} color={STATUS_COLOR[onlineStatus] || 'default'} />
                      </TableCell>
                      <TableCell>
                        <Chip
                          size="small"
                          label={driver.isVerified ? 'Verified' : 'Pending'}
                          color={driver.isVerified ? 'success' : 'warning'}
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell align="right" onClick={(e) => e.stopPropagation()}>
                        {!driver.isVerified && (
                          <Tooltip title="Approve">
                            <IconButton
                              size="small"
                              color="success"
                              onClick={() => {
                                if (window.confirm(`Approve driver ${driver.firstName}?`)) approveMutation.mutate(driver._id);
                              }}
                            >
                              <CheckCircle fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                        <Tooltip title={driver.partnerStatus === 'blocked' ? 'Unblock' : 'Block'}>
                          <IconButton
                            size="small"
                            color={driver.partnerStatus === 'blocked' ? 'success' : 'error'}
                            onClick={() => {
                              const isBlocked = driver.partnerStatus === 'blocked';
                              if (window.confirm(`${isBlocked ? 'Unblock' : 'Block'} driver ${driver.firstName}?`)) {
                                blockMutation.mutate({ id: driver._id, block: !isBlocked });
                              }
                            }}
                          >
                            <Block fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  );
                })}
                {!drivers.length && (
                  <TableRow>
                    <TableCell colSpan={9} align="center" sx={{ py: 5 }}>
                      <Typography color="text.secondary">No drivers found matching the filters.</Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            component="div"
            count={total}
            page={page}
            onPageChange={(_, p) => setPage(p)}
            rowsPerPage={pageSize}
            onRowsPerPageChange={(e) => { setPageSize(parseInt(e.target.value, 10)); setPage(0); }}
            rowsPerPageOptions={[10, 25, 50, 100]}
          />
        </Paper>
      )}

      {/* Driver Detail Modal */}
      <Dialog open={!!detailDriver} onClose={() => setDetailDriver(null)} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Typography variant="h6" fontWeight={700}>
              {detailDriver?.firstName} {detailDriver?.lastName}
            </Typography>
            <IconButton size="small" onClick={() => setDetailDriver(null)}><Close /></IconButton>
          </Stack>
        </DialogTitle>
        <DialogContent>
          {detailDriver && (
            <Stack spacing={2}>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">Phone</Typography>
                  <Typography variant="body2" fontWeight={600}>{detailDriver.phone || '—'}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">Email</Typography>
                  <Typography variant="body2">{detailDriver.email || '—'}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">Zone</Typography>
                  <Typography variant="body2">{detailDriver.zoneId?.name || '—'}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">Rating</Typography>
                  <Typography variant="body2">
                    {detailDriver.rating != null ? `${detailDriver.rating.toFixed(1)} ★` : '—'}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">Wallet Balance</Typography>
                  <Typography variant="body2" fontWeight={600}>{formatMoney(detailDriver.walletBalance || 0)}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">Status</Typography>
                  <Box mt={0.5}>
                    <Chip
                      size="small"
                      label={driverOnlineStatus(detailDriver)}
                      color={STATUS_COLOR[driverOnlineStatus(detailDriver)] || 'default'}
                    />
                  </Box>
                </Grid>
              </Grid>
              <Divider />
              {detailDriver.vehicleDetails?.model && (
                <Box>
                  <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>Vehicle</Typography>
                  <Grid container spacing={1}>
                    <Grid item xs={6}>
                      <Typography variant="caption" color="text.secondary">Model</Typography>
                      <Typography variant="body2">{detailDriver.vehicleDetails.model}</Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="caption" color="text.secondary">Number</Typography>
                      <Typography variant="body2">{detailDriver.vehicleDetails.number}</Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="caption" color="text.secondary">Type</Typography>
                      <Typography variant="body2">{detailDriver.vehicleDetails.type}</Typography>
                    </Grid>
                  </Grid>
                </Box>
              )}
              <Divider />
              <Box>
                <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>Documents</Typography>
                <Chip
                  size="small"
                  label={detailDriver.isVerified ? 'Documents Verified' : 'Verification Pending'}
                  color={detailDriver.isVerified ? 'success' : 'warning'}
                />
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">Member since</Typography>
                <Typography variant="body2">
                  {detailDriver.createdAt ? new Date(detailDriver.createdAt).toLocaleDateString() : '—'}
                </Typography>
              </Box>
              <Stack direction="row" spacing={1} justifyContent="flex-end">
                {!detailDriver.isVerified && (
                  <Button
                    size="small"
                    variant="contained"
                    color="success"
                    onClick={() => {
                      approveMutation.mutate(detailDriver._id);
                      setDetailDriver(null);
                    }}
                  >
                    Approve
                  </Button>
                )}
                <Button
                  size="small"
                  variant="outlined"
                  color={detailDriver.partnerStatus === 'blocked' ? 'success' : 'error'}
                  onClick={() => {
                    const isBlocked = detailDriver.partnerStatus === 'blocked';
                    blockMutation.mutate({ id: detailDriver._id, block: !isBlocked });
                    setDetailDriver(null);
                  }}
                >
                  {detailDriver.partnerStatus === 'blocked' ? 'Unblock' : 'Block'}
                </Button>
              </Stack>
            </Stack>
          )}
        </DialogContent>
      </Dialog>
    </PageShell>
  );
}
